/**
 * BLE Protocol Encoding/Decoding Utilities
 * Handles binary data conversion for M-Scale BLE characteristics
 */

import {
  DeviceLinkCommand,
  DeviceRole,
  SystemCommand,
  CalibrationCommand,
  type HardwareConfig,
  type ScaleWeight,
  type PairInfo,
  type TargetRange,
  type SystemStatus,
  type CalibrationStatus,
  type CalibrationData,
  type PositionMapEntry,
  type LEDOverride,
  type DeviceLinkStatus,
  type DeviceLinkConfig,
} from './types'

/**
 * Decode all weights from binary data
 * Format: 32x Float32 (128 bytes)
 */
export function decodeAllWeights(dataView: DataView): number[] {
  const weights: number[] = []
  for (let i = 0; i < 32; i++) {
    const offset = i * 4
    const weight = dataView.getFloat32(offset, true) // little-endian
    weights.push(weight)
  }
  return weights
}

/**
 * Decode single weight reading
 * Format: [position: Uint8, weight: Float32]
 */
export function decodeSingleWeight(dataView: DataView): ScaleWeight {
  return {
    position: dataView.getUint8(0),
    weight: dataView.getFloat32(1, true),
  }
}

/**
 * Decode group data
 * Format: 4 groups x 20 bytes = 80 bytes
 * Each group contains:
 *   offset+0: groupId (0-3)
 *   offset+1: groupSize (2-4)
 *   offset+2: position[0]
 *   offset+3: position[1]
 *   offset+4: position[2] (0xFF if not used)
 *   offset+5: position[3] (0xFF if not used)
 *   offset+6-9: totalWeight (float)
 *   offset+10: isValid (0x01 = valid)
 *   offset+11-19: reserved/unused
 */
export function decodePairData(dataView: DataView): PairInfo[] {
  const groups: PairInfo[] = []

  for (let i = 0; i < 4; i++) {
    const offset = i * 20
    const groupId = dataView.getUint8(offset)
    const groupSize = dataView.getUint8(offset + 1)

    // groupSize of 0 indicates no group
    if (groupSize === 0 || groupSize > 4) {
      continue
    }

    // Read all positions (up to 4)
    const positions: number[] = []
    for (let j = 0; j < 4; j++) {
      const pos = dataView.getUint8(offset + 2 + j)
      if (j < groupSize && pos !== 0xFF) {
        positions.push(pos)
      }
    }

    // Skip if we don't have at least 2 valid positions
    if (positions.length < 2) {
      continue
    }

    const totalWeight = dataView.getFloat32(offset + 6, true)
    const isValid = dataView.getUint8(offset + 10) === 0x01

    // Estimate individual weights (equal distribution)
    const avgWeight = totalWeight / positions.length
    const weights = positions.map(() => avgWeight)

    groups.push({
      positions,
      weights,
      totalWeight,
      isValid,
      groupId,
    })
  }

  return groups
}

/**
 * Decode target range
 * Format: [min: Float32, max: Float32]
 */
export function decodeTargetRange(dataView: DataView): TargetRange {
  return {
    min: dataView.getFloat32(0, true),
    max: dataView.getFloat32(4, true),
  }
}

/**
 * Encode target range
 */
export function encodeTargetRange(range: TargetRange): ArrayBuffer {
  const buffer = new ArrayBuffer(8)
  const view = new DataView(buffer)
  view.setFloat32(0, range.min, true)
  view.setFloat32(4, range.max, true)
  return buffer
}

/**
 * Decode group size
 * Format: [groupSize: Uint8]
 */
export function decodeGroupSize(dataView: DataView): number {
  return dataView.getUint8(0)
}

/**
 * Encode group size
 */
export function encodeGroupSize(size: number): ArrayBuffer {
  const buffer = new ArrayBuffer(1)
  const view = new DataView(buffer)
  view.setUint8(0, size)
  return buffer
}

/**
 * Decode minimum ratio
 * Format: [minRatio: Float32]
 */
export function decodeMinRatio(dataView: DataView): number {
  return dataView.getFloat32(0, true)
}

/**
 * Encode minimum ratio
 */
export function encodeMinRatio(ratio: number): ArrayBuffer {
  const buffer = new ArrayBuffer(4)
  const view = new DataView(buffer)
  view.setFloat32(0, ratio, true)
  return buffer
}

/**
 * Decode system status
 * Format: [mode: Uint8, operation: Uint8, uptime: Uint32, errorCode: Uint8]
 */
export function decodeSystemStatus(dataView: DataView): SystemStatus {
  return {
    mode: dataView.getUint8(0),
    operation: dataView.getUint8(1),
    uptime: dataView.getUint32(2, true),
    errorCode: dataView.getUint8(6),
  }
}

/**
 * Encode system command
 */
export function encodeSystemCommand(command: SystemCommand): ArrayBuffer {
  const buffer = new ArrayBuffer(1)
  const view = new DataView(buffer)
  view.setUint8(0, command)
  return buffer
}

/**
 * Encode tare command
 * @param position - 0xFF for all scales, 0-31 for specific position
 */
export function encodeTareCommand(position: number = 0xFF): ArrayBuffer {
  const buffer = new ArrayBuffer(1)
  const view = new DataView(buffer)
  view.setUint8(0, position)
  return buffer
}

/**
 * Encode calibration command
 */
export function encodeCalibrationCommand(
  position: number,
  command: CalibrationCommand,
  knownWeight?: number
): ArrayBuffer {
  const buffer = new ArrayBuffer(6)
  const view = new DataView(buffer)
  view.setUint8(0, position)
  view.setUint8(1, command)
  if (knownWeight !== undefined) {
    view.setFloat32(2, knownWeight, true)
  }
  return buffer
}

/**
 * Decode calibration status
 */
export function decodeCalibrationStatus(dataView: DataView): CalibrationStatus {
  return {
    position: dataView.getUint8(0),
    step: dataView.getUint8(1),
    currentRawReading: dataView.getInt32(2, true),
    progressPercent: dataView.getUint8(6),
  }
}

/**
 * Decode calibration data for a single position
 */
export function decodeCalibrationData(dataView: DataView): CalibrationData {
  const flags = dataView.getUint8(1)
  return {
    position: dataView.getUint8(0),
    isActive: (flags & 0x01) !== 0,
    isCalibrated: (flags & 0x02) !== 0,
    zeroOffset: dataView.getInt32(2, true),
    calibrationFactor: dataView.getFloat32(6, true),
    row: dataView.getUint8(10),
    col: dataView.getUint8(11),
  }
}

/**
 * Decode position map
 * Format: 32 positions x 4 bytes = 128 bytes
 */
export function decodePositionMap(dataView: DataView): PositionMapEntry[] {
  const entries: PositionMapEntry[] = []

  for (let i = 0; i < 32; i++) {
    const offset = i * 4
    const flags = dataView.getUint8(offset + 3)

    entries.push({
      row: dataView.getUint8(offset),
      col: dataView.getUint8(offset + 1),
      ledIndex: dataView.getUint8(offset + 2),
      hasLED: (flags & 0x01) !== 0,
    })
  }

  return entries
}

/**
 * Decode hardware config from JSON string
 */
export function decodeHardwareConfig(jsonString: string): HardwareConfig {
  return JSON.parse(jsonString) as HardwareConfig
}

/**
 * Encode LED override command
 */
export function encodeLEDOverride(override: LEDOverride): ArrayBuffer {
  const buffer = new ArrayBuffer(5)
  const view = new DataView(buffer)
  view.setUint8(0, override.position)
  view.setUint8(1, override.r)
  view.setUint8(2, override.g)
  view.setUint8(3, override.b)
  view.setUint8(4, override.duration)
  return buffer
}

/**
 * Helper: Convert ArrayBuffer to hex string (for debugging)
 */
export function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join(' ')
}

/**
 * Helper: Convert DataView to text (for string characteristics)
 */
export function dataViewToString(dataView: DataView): string {
  const decoder = new TextDecoder()
  return decoder.decode(dataView.buffer)
}

/**
 * Helper: Convert string to ArrayBuffer (for writing)
 */
export function stringToBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder()
  return encoder.encode(str).buffer
}

// ===== Custom Device Name Suffix =====

/**
 * Decode custom suffix (4 hex characters)
 * Format: 4-byte ASCII string
 */
export function decodeCustomSuffix(dataView: DataView): string {
  return dataViewToString(dataView).trim()
}

/**
 * Encode custom suffix (must be exactly 4 uppercase hex characters)
 * Format: 4-byte ASCII string
 */
export function encodeCustomSuffix(suffix: string): ArrayBuffer {
  // Validate suffix format
  if (suffix.length !== 4) {
    throw new Error('Custom suffix must be exactly 4 characters')
  }

  const upperSuffix = suffix.toUpperCase()
  if (!/^[0-9A-F]{4}$/.test(upperSuffix)) {
    throw new Error('Custom suffix must contain only hex characters (0-9, A-F)')
  }

  return stringToBuffer(upperSuffix)
}

// ===== Device Link (Dual-Device Mode) =====

/**
 * Decode device link status
 * Format: [linkActive: Uint8, role: Uint8, linkedDeviceName: string(32), linkedFirmwareVersion: Uint32]
 * Total: 40 bytes
 */
export function decodeDeviceLinkStatus(dataView: DataView): DeviceLinkStatus {
  const linkActive = dataView.getUint8(0) !== 0
  const role = dataView.getUint8(1) as DeviceRole

  // Extract device name (32 bytes, null-terminated string)
  const nameBytes = new Uint8Array(dataView.buffer, 2, 32)
  const nameEndIndex = nameBytes.indexOf(0)
  const nameSlice = nameEndIndex >= 0 ? nameBytes.slice(0, nameEndIndex) : nameBytes
  const linkedDeviceName = new TextDecoder().decode(nameSlice)

  const linkedFirmwareVersion = dataView.getUint32(34, true) // little-endian

  return {
    linkActive,
    role,
    linkedDeviceName,
    linkedFirmwareVersion,
  }
}

/**
 * Encode device link control command
 * Format: [command: Uint8, secondaryAddress: 6 bytes (MAC address)]
 * Total: 7 bytes (or 1 byte for commands without address)
 */
export function encodeDeviceLinkControl(
  command: DeviceLinkCommand,
  secondaryAddress?: number[]
): Uint8Array {
  if (command === DeviceLinkCommand.CONNECT_TO_SECONDARY && secondaryAddress) {
    // Connect command needs address
    const buffer = new Uint8Array(7)
    buffer[0] = command
    for (let i = 0; i < 6; i++) {
      buffer[i + 1] = secondaryAddress[i]
    }
    return buffer
  } else {
    // Disconnect or query commands
    const buffer = new Uint8Array(1)
    buffer[0] = command
    return buffer
  }
}

/**
 * Decode device link configuration
 * Format: [relativePosition: Uint8, primaryRotation: Uint8, secondaryRotation: Uint8]
 * Total: 3 bytes
 */
export function decodeDeviceLinkConfig(dataView: DataView): DeviceLinkConfig {
  const relativePositionMap = ['right', 'left', 'below', 'above'] as const
  const rotationMap = [0, 90, 180, 270] as const

  const relativePosition = relativePositionMap[dataView.getUint8(0)] || 'right'
  const primaryRotation = rotationMap[dataView.getUint8(1)] || 0
  const secondaryRotation = rotationMap[dataView.getUint8(2)] || 0

  return {
    relativePosition,
    primaryRotation,
    secondaryRotation,
  }
}

/**
 * Encode device link configuration
 * Format: [relativePosition: Uint8, primaryRotation: Uint8, secondaryRotation: Uint8]
 * Total: 3 bytes
 */
export function encodeDeviceLinkConfig(config: DeviceLinkConfig): Uint8Array {
  const buffer = new Uint8Array(3)

  // Encode relative position
  const positionMap: Record<string, number> = {
    right: 0,
    left: 1,
    below: 2,
    above: 3,
  }
  buffer[0] = positionMap[config.relativePosition] || 0

  // Encode rotations
  const rotationMap: Record<number, number> = {
    0: 0,
    90: 1,
    180: 2,
    270: 3,
  }
  buffer[1] = rotationMap[config.primaryRotation] || 0
  buffer[2] = rotationMap[config.secondaryRotation] || 0

  return buffer
}
